import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from "@akuna/ui";
import { Label } from '@/components/ui/label';
import { Button } from "@akuna/ui";
import { Autocomplete } from '@/components/ui/autocomplete';
import type { AutocompleteOption } from '@/components/ui/autocomplete';
import { User, X } from 'lucide-react';
import { usePatientSearchForSale } from '@/hooks/usePatientSearchForSale';
import { formatCpf } from '@/lib/utils/cpf-mask';
import { supabase } from '@/integrations/supabase/client';
import type { SaleContext } from '@/hooks/useSales';
import type { SuggestedPackageCharge } from '@/lib/utils/suggested-package-charge';
import { useTranslation } from 'react-i18next';
import { SalePetsSummary } from './SalePetsSummary';
import type { CartPetSummary } from '@/lib/sales/cart-pets';

export interface PrefillFromAppointment {
  appointmentId: string | null;
  petId: string;
  ownerId: string;
  petName: string;
  ownerName: string;
  /** When the appointment has multiple patients, list them for display. Sale remains linked to the primary (petId). */
  pets?: { petId: string; petName: string }[];
  suggestedPackageCharges?: SuggestedPackageCharge[];
}

interface PatientAndCpfSectionProps {
  saleContext: SaleContext;
  onSetPetId: (id: string | null) => void;
  onSetOwnerId: (id: string | null) => void;
  onSetBuyerCpf: (value: string | null) => void;
  onSetAppointmentId?: (id: string | null) => void;
  prefillFromAppointment?: PrefillFromAppointment | null;
  onPrefillApplied?: () => void;
  variant?: 'card' | 'embedded';
  /** Pets da venda (derivado do carrinho); só leitura. */
  petsSummary?: CartPetSummary[];
}

export const PatientAndCpfSection = ({
  saleContext,
  onSetPetId,
  onSetOwnerId,
  onSetBuyerCpf,
  onSetAppointmentId,
  prefillFromAppointment,
  onPrefillApplied,
  variant = 'card',
  petsSummary = [],
}: PatientAndCpfSectionProps) => {
  const { t } = useTranslation("erp");
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [selectedOwnerName, setSelectedOwnerName] = useState<string | null>(null);
  const [registeredOwnerCpf, setRegisteredOwnerCpf] = useState<string | null>(null);
  const prefillAppliedRef = useRef(false);
  const { data: patientRows = [] } = usePatientSearchForSale(patientSearch);

  const getDisplayLabel = useCallback((input: {
    petName?: string;
    ownerName?: string;
    pets?: { petId: string; petName: string }[];
  }) => {
    const ownerName = (input.ownerName ?? '').trim();
    const petNames = (input.pets?.length ? input.pets.map((p) => p.petName) : [input.petName ?? ''])
      .map((name) => name.trim())
      .filter(Boolean);

    if (petNames.length === 0 && !ownerName) return null;
    if (petNames.length === 0) return ownerName;
    if (!ownerName) return petNames.join(', ');
    return `${petNames.join(', ')} – ${ownerName}`;
  }, []);

  const patientOptions: AutocompleteOption[] = useMemo(
    () =>
      patientRows.map((row) => ({
        label: row.pet_record_number
          ? t("patientCpf.patientWithRecord", { petName: row.pet_name, recordNumber: row.pet_record_number, ownerName: row.owner_name })
          : `${row.pet_name} – ${row.owner_name}`,
        value: `${row.owner_id}-${row.pet_id}`,
      })),
    [patientRows, t]
  );

  useEffect(() => {
    if (!prefillFromAppointment) {
      prefillAppliedRef.current = false;
      return;
    }
    if (prefillAppliedRef.current) return;
    let cancelled = false;
    const { appointmentId, petId, ownerId, petName, ownerName, pets } = prefillFromAppointment;
    onSetAppointmentId?.(appointmentId);
    onSetPetId(petId);
    onSetOwnerId(ownerId);
    const displayLabel = getDisplayLabel({ petName, ownerName, pets });
    setSelectedLabel(displayLabel);
    setSelectedOwnerName((ownerName ?? '').trim() || null);
    prefillAppliedRef.current = true;
    onPrefillApplied?.();

    (async () => {
      const needsOwnerName = !(ownerName ?? '').trim();
      const petNamesFromPrefill = (pets?.length ? pets.map((p) => p.petName) : [petName ?? ''])
        .map((name) => name.trim())
        .filter(Boolean);
      const needsPetNames = petNamesFromPrefill.length === 0;

      const [{ data: owner }, petResult] = await Promise.all([
        supabase.from('owners').select('name, document').eq('id', ownerId).single(),
        needsPetNames
          ? pets?.length
            ? supabase.from('pets').select('id, name').in('id', pets.map((p) => p.petId))
            : supabase.from('pets').select('name').eq('id', petId).single()
          : Promise.resolve({ data: null } as { data: null }),
      ]);

      if (cancelled) return;

      const docDigits = (owner?.document ?? '').toString().replace(/\D/g, '');
      setRegisteredOwnerCpf(docDigits.length === 11 ? formatCpf(docDigits) : null);

      if (needsOwnerName || needsPetNames) {
        const resolvedOwnerName = (ownerName ?? '').trim() || (owner?.name ?? '').trim();
        const petResultRows = pets?.length
          ? ((petResult as { data?: Array<{ id: string; name?: string | null }> | null }).data ?? [])
          : null;
        const petResultSingle = !pets?.length
          ? ((petResult as { data?: { name?: string | null } | null }).data ?? null)
          : null;

        const resolvedPets = pets?.length
          ? pets.map((p) => {
              const fallbackName =
                petResultRows?.find((row) => row.id === p.petId)?.name?.toString().trim() ?? '';
              return {
                petId: p.petId,
                petName: (p.petName ?? '').toString().trim() || fallbackName,
              };
            })
          : undefined;

        const resolvedPetName = pets?.length
          ? ''
          : (petName ?? '').toString().trim() || (petResultSingle?.name ?? '').toString().trim();

        const nextLabel = getDisplayLabel({
          petName: resolvedPetName,
          ownerName: resolvedOwnerName,
          pets: resolvedPets,
        });

        if (nextLabel) setSelectedLabel(nextLabel);
        if (resolvedOwnerName) setSelectedOwnerName(resolvedOwnerName);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    prefillFromAppointment,
    onSetPetId,
    onSetOwnerId,
    onSetBuyerCpf,
    onSetAppointmentId,
    onPrefillApplied,
    getDisplayLabel,
  ]);

  useEffect(() => {
    // Avoid clearing right after `clearCart()` when a prefill is about to set pet/owner in the next render.
    if (!saleContext.petId && !saleContext.ownerId && !prefillFromAppointment) {
      prefillAppliedRef.current = false;
      setSelectedLabel(null);
      setSelectedOwnerName(null);
      setRegisteredOwnerCpf(null);
      setPatientSearch('');
    }
  }, [saleContext.petId, saleContext.ownerId, prefillFromAppointment]);

  const handleSelectPatient = useCallback(
    (row: { owner_id: string; pet_id: string; owner_name: string; pet_name: string; owner_document: string | null }) => {
      onSetAppointmentId?.(null);
      onSetPetId(row.pet_id);
      onSetOwnerId(row.owner_id);
      setSelectedLabel(`${row.pet_name} – ${row.owner_name}`);
      setSelectedOwnerName(row.owner_name.trim() || null);
      setPatientSearch('');
      const doc = (row.owner_document ?? '').replace(/\D/g, '');
      setRegisteredOwnerCpf(doc.length === 11 ? formatCpf(doc) : null);
    },
    [onSetAppointmentId, onSetPetId, onSetOwnerId]
  );

  const handleClearPatient = useCallback(() => {
    onSetAppointmentId?.(null);
    onSetPetId(null);
    onSetOwnerId(null);
    onSetBuyerCpf(null);
    setSelectedLabel(null);
    setSelectedOwnerName(null);
    setRegisteredOwnerCpf(null);
    setPatientSearch('');
  }, [onSetAppointmentId, onSetPetId, onSetOwnerId, onSetBuyerCpf]);

  const handlePatientSelect = useCallback(
    (option: AutocompleteOption) => {
      const row = patientRows.find((r) => `${r.owner_id}-${r.pet_id}` === option.value);
      if (row) handleSelectPatient(row);
    },
    [patientRows, handleSelectPatient]
  );

  const handleCpfChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatCpf(e.target.value);
      onSetBuyerCpf(formatted || null);
    },
    [onSetBuyerCpf]
  );

  const canUseRegisteredCpf = Boolean(registeredOwnerCpf && registeredOwnerCpf !== saleContext.buyerCpf);

  const header = (
    <>
      <div className="flex items-center gap-2">
        <User className="h-5 w-5" />
        <h3 className="text-lg font-semibold">{t("patientCpf.title")}</h3>
      </div>
    </>
  );

  const content = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("patientCpf.ownerLabel")}</Label>
        {selectedLabel ? (
          <div className="flex items-center justify-between p-3 bg-secondary rounded-md">
            <span className="text-sm font-medium truncate">{selectedOwnerName ?? selectedLabel}</span>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={handleClearPatient}
              aria-label={t("patientCpf.clearPatient")}
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        ) : (
          <Autocomplete
            options={patientOptions}
            value={patientSearch}
            onChange={setPatientSearch}
            onSelect={handlePatientSelect}
            disableLocalFilter
            placeholder={t("patientCpf.patientPlaceholder")}
            emptyMessage={t("patientCpf.patientEmpty")}
          />
        )}
      </div>
      {selectedLabel ? <SalePetsSummary pets={petsSummary} /> : null}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label>{t("patientCpf.cpfLabel")}</Label>
          {canUseRegisteredCpf ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (registeredOwnerCpf) onSetBuyerCpf(registeredOwnerCpf);
              }}
            >
              {t("patientCpf.useRegisteredCpf")}
            </Button>
          ) : null}
        </div>
        <Input
          placeholder="000.000.000-00"
          value={saleContext.buyerCpf ?? ''}
          onChange={handleCpfChange}
          maxLength={14}
        />
      </div>
    </div>
  );

  if (variant === 'embedded') {
    return (
      <div className="space-y-4">
        <div className="space-y-1">{header}</div>
        {content}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          {t("patientCpf.cardTitle")}
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          {t("patientCpf.cardDescription")}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {content}
      </CardContent>
    </Card>
  );
};
